import { inject } from '@angular/core';
import { Route, Router, type CanDeactivateFn } from '@angular/router';
import { Environment, ENVIRONMENT } from '@ld.consumer-portal/models';
import {
    alreadyAuthenticatedGuard,
    authGuard,
    blockIfNotUnderMaintenance,
    canDeactivateAutopay,
    dashboardDataResolver,
    externalAppLaunchGuard,
    externalCallbackGuard,
    jvDashboardRedirectGuard,
    maintenanceGuard,
    onboardingGuard,
    originationAuthGuard,
    originationVisibilityGuard,
    passwordResetRedirectGuard,
    payoffQuoteGuard,
    signOutGuard,
    updatePasswordGuard,
    wizardResumeGuard
} from '@ld.consumer-portal/routing';
import { screenshotGalleryGuard } from '@ld.consumer-portal/screenshots';
import { styleGuideGuard } from '@ld.consumer-portal/style-guide';

const originationTemplateBuilderGuard = () => {
  const environment = inject<Environment>(ENVIRONMENT);
  const router = inject(Router);

  return environment.appSettings.enableOriginationTemplateBuilder === true ? true : router.parseUrl('/dashboard');
};

interface UnsavedChangesComponent {
  canDeactivate?: () => boolean;
}

const unsavedChangesGuard: CanDeactivateFn<UnsavedChangesComponent> = (component) =>
  component.canDeactivate?.() ?? true;

export const appRoutes: Route[] = [
  {
    path: 'onboarding',
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.OnboardingComponent),
    data: { minimalHeader: true },
  },
  {
    path: 'auth',
    canActivate: [onboardingGuard],
    loadChildren: () => import('@ld.consumer-portal/auth').then((m) => m.authRoutes),
  },
  {
    path: 'callback',
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.CallbackComponent),
  },
  {
    path: 'ciam/callback',
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.CiamCallbackComponent),
  },
  {
    path: 'login',
    canActivate: [onboardingGuard, maintenanceGuard, alreadyAuthenticatedGuard],
    loadChildren: () => import('@ld.consumer-portal/auth').then((m) => m.authRoutes),
  },
  {
    path: 'register',
    redirectTo: 'auth/register',
    pathMatch: 'full',
  },
  {
    path: 'sign-out',
    canActivate: [signOutGuard],
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.LoginComponent),
  },
  {
    path: 'update-password/password-reset/failure',
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.ResetPasswordFailureComponent),
    data: { minimalHeader: true },
  },
  {
    path: 'update-password/password-reset/:token',
    canActivate: [passwordResetRedirectGuard],
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.ResetPasswordComponent),
    data: { minimalHeader: true },
  },
  {
    path: 'update-password',
    canActivate: [updatePasswordGuard],
    children: [],
  },
  {
    path: 'dashboard',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.DashboardLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/core').then((m) => m.dashboardRoutes),
      },
    ],
  },
  {
    path: 'origination/:loanId',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.DashboardLayoutComponent),
    canMatch: [originationVisibilityGuard],
    canActivate: [maintenanceGuard, authGuard, originationAuthGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/origination').then((m) => m.originationRoutes),
      },
    ],
  },
  {
    path: 'verify-employments',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.FrontPorchLayoutComponent),
    canMatch: [originationVisibilityGuard],
    canActivate: [maintenanceGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/origination').then((m) => m.frontPorchVoxRoutes),
      },
    ],
  },
  {
    path: 'verify-assets',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.FrontPorchLayoutComponent),
    canMatch: [originationVisibilityGuard],
    canActivate: [maintenanceGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/origination').then((m) => m.frontPorchVoaRoutes),
      },
    ],
  },
  {
    path: 'apply/refinance',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.DashboardLayoutComponent),
    canMatch: [originationVisibilityGuard],
    canActivate: [maintenanceGuard, authGuard, wizardResumeGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/origination').then((m) => m.refinanceApplicationRoutes),
      },
    ],
  },
  {
    path: 'apply/purchase',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.DashboardLayoutComponent),
    canMatch: [originationVisibilityGuard],
    canActivate: [maintenanceGuard, authGuard, wizardResumeGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/origination').then((m) => m.purchaseApplicationRoutes),
      },
    ],
  },
  {
    path: 'loans/:accountId',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.LoanLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        redirectTo: 'details',
        pathMatch: 'full',
      },
      {
        path: 'details',
        loadComponent: () => import('@ld.consumer-portal/account-details').then((m) => m.AccountDetailsComponent),
      },
      {
        path: 'autopay',
        loadComponent: () => import('@ld.consumer-portal/payments').then((m) => m.AutopayComponent),
        canDeactivate: [canDeactivateAutopay],
      },
      {
        path: 'payment/edit/:paymentId',
        loadComponent: () => import('@ld.consumer-portal/payments').then((m) => m.MakePaymentComponent),
        data: { isEditMode: true },
      },
      {
        path: 'payment',
        loadComponent: () => import('@ld.consumer-portal/payments').then((m) => m.MakePaymentComponent),
      },
      {
        path: 'payoff-quote',
        canActivate: [payoffQuoteGuard],
        loadComponent: () => import('@ld.consumer-portal/payments').then((m) => m.PayoffQuoteComponent),
      },
      {
        path: 'documents',
        loadComponent: () => import('@ld.consumer-portal/documents').then((m) => m.StatementsDocumentsComponent),
      },
      {
        path: 'manage-accounts',
        loadComponent: () => import('@ld.consumer-portal/payments').then((m) => m.ManageAccountsComponent),
      },
      {
        path: 'request-funds',
        loadComponent: () => import('@ld.consumer-portal/payments').then((m) => m.RequestFundsComponent),
      },
    ],
  },
  {
    path: 'manage-paperless/:accountId',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.LoanLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadComponent: () => import('@ld.consumer-portal/preferences').then((m) => m.PaperlessSettingsComponent),
      },
    ],
  },
  {
    path: 'unsubscribe-paperless/:loanNumber',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.DashboardLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    data: { stickyFooter: true },
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/preferences').then((m) => m.unsubscribePaperlessRoutes),
      },
    ],
  },
  {
    path: 'autopay-consent',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.DashboardLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/consent').then((m) => m.autopayConsentRoutes),
      },
    ],
  },
  {
    path: 'contact-information/:accountId',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.LoanLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/preferences').then((m) => m.profileSettingsRoutes),
      },
    ],
  },
  {
    path: 'messages/:accountId',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.LoanLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/documents').then((m) => m.MESSAGE_CENTER_ROUTES),
      },
    ],
  },
  {
    path: 'smc',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'help/:accountId/faqs',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.LoanLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadComponent: () => import('@ld.consumer-portal/ui-core').then((m) => m.HelpFaqComponent),
      },
    ],
  },
  {
    path: 'help/:accountId/contact-us',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.LoanLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadComponent: () => import('@ld.consumer-portal/ui-core').then((m) => m.ContactUsPageComponent),
      },
    ],
  },
  {
    path: 'help/:accountId/property-taxes',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.LoanLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadComponent: () => import('@ld.consumer-portal/ui-core').then((m) => m.PropertyTaxesPageComponent),
      },
    ],
  },
  {
    path: 'help/:accountId/homeowners-insurance',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.LoanLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadComponent: () => import('@ld.consumer-portal/ui-core').then((m) => m.HomeownersInsurancePageComponent),
      },
    ],
  },
  {
    path: 'help/:accountId/insurance-claims',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.LoanLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadComponent: () => import('@ld.consumer-portal/ui-core').then((m) => m.InsuranceClaimsPageComponent),
      },
    ],
  },
  {
    path: 'help/:accountId/financial-hardship',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.LoanLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadComponent: () => import('@ld.consumer-portal/ui-core').then((m) => m.FinancialHardshipPageComponent),
      },
    ],
  },
  {
    path: 'faqs',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'contact-us',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'property-taxes',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'unsubscribe-email',
    loadComponent: () => import('@ld.consumer-portal/preferences').then((m) => m.UnsubscribeEmailComponent),
  },
  {
    path: 'error',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.ErrorPageComponent),
  },
  {
    path: 'service-unavailable',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.ServiceUnavailableComponent),
  },
  {
    path: 'cookies-disabled',
    loadComponent: () => import('@ld.consumer-portal/ui-core').then((m) => m.CookiesDisabledPageComponent),
  },
  {
    path: 'maintenance',
    loadComponent: () => import('@ld.consumer-portal/ui-core').then((m) => m.MaintenancePageComponent),
  },
  {
    path: 'underMaintenance',
    canActivate: [blockIfNotUnderMaintenance],
    loadComponent: () => import('@ld.consumer-portal/ui-core').then((m) => m.UnderMaintenancePageComponent),
  },
  {
    path: 'external/:externalPath',
    canActivate: [externalAppLaunchGuard],
    children: [],
  },
  {
    path: 'external-callback',
    canActivate: [externalCallbackGuard],
    children: [],
  },
  {
    path: 'push-notifications',
    canActivate: [maintenanceGuard, authGuard],
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.PushNotificationsComponent),
  },
  {
    path: 'feedback',
    canActivate: [maintenanceGuard, authGuard],
    loadChildren: () => import('@ld.consumer-portal/preferences').then((m) => m.feedbackRoutes),
  },
  {
    path: 'calculators',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.DashboardLayoutComponent),
    canActivate: [maintenanceGuard, authGuard, jvDashboardRedirectGuard],
    resolve: { dashboardData: dashboardDataResolver },
    children: [
      {
        path: '',
        loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.CalculatorsPageComponent),
      },
    ],
  },
  {
    path: 'products',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'screenshots',
    loadComponent: () => import('@ld.consumer-portal/screenshots').then((m) => m.ScreenshotGalleryComponent),
    canActivate: [screenshotGalleryGuard],
    data: { title: 'Screenshot Gallery' },
  },
  {
    path: 'style-guide',
    loadComponent: () => import('@ld.consumer-portal/style-guide').then((m) => m.StyleGuidePageComponent),
    canActivate: [styleGuideGuard],
    data: { title: 'Style Guide' },
  },
  {
    path: 'template-builder/purchase',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.DashboardLayoutComponent),
    canActivate: [maintenanceGuard, originationTemplateBuilderGuard],
    data: { title: 'Template Builder Test Flow' },
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/origination').then((m) => m.purchaseApplicationRoutes),
      },
    ],
  },
  {
    path: 'template-builder/refinance',
    loadComponent: () => import('@ld.consumer-portal/core').then((m) => m.DashboardLayoutComponent),
    canActivate: [maintenanceGuard, originationTemplateBuilderGuard],
    data: { title: 'Template Builder Test Flow' },
    children: [
      {
        path: '',
        loadChildren: () => import('@ld.consumer-portal/origination').then((m) => m.refinanceApplicationRoutes),
      },
    ],
  },
  {
    path: 'template-builder',
    loadComponent: () => import('@ld.consumer-portal/origination').then((m) => m.OriginationTemplateBuilderComponent),
    canActivate: [maintenanceGuard, originationTemplateBuilderGuard],
    canDeactivate: [unsavedChangesGuard],
    data: { title: 'Template Builder' },
  },
  {
    path: 'modal-gallery',
    loadComponent: () => import('@ld.consumer-portal/modals').then((m) => m.ModalGalleryComponent),
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.ForgotPasswordComponent),
    data: { minimalHeader: true },
  },
  {
    path: 'forgot-password-success',
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.ForgotPasswordSuccessComponent),
    data: { minimalHeader: true },
  },
  {
    path: 'password-reset/failure',
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.ResetPasswordFailureComponent),
    data: { minimalHeader: true },
  },
  {
    path: 'password-reset/:token',
    canActivate: [passwordResetRedirectGuard],
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.ResetPasswordComponent),
    data: { minimalHeader: true },
  },
  {
    path: 'sso-landing',
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.SsoLandingComponent),
  },
  {
    path: 'Account/ConfirmRegister',
    loadComponent: () => import('@ld.consumer-portal/auth').then((m) => m.ConfirmRegisterComponent),
    data: { title: 'Confirm Registration' },
  },
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
